import React from 'react';
import { AlertTriangle, Clock, Sunrise, Users, Droplet, Coffee } from 'lucide-react';

const QuickGuide: React.FC = () => {
  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <div className="mb-4">
        <h3 className="text-xl font-semibold mb-2">Morning Routine Automation Overview</h3>
        <p className="text-gray-600">
          This dashboard demonstrates a comprehensive smart home solution that helps users start their day 
          more efficiently through automated routines that respond to time, sensors, and user preferences.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FeatureCard 
          title="Bed Exit Detection" 
          icon={<AlertTriangle className="text-blue-500" />}
          description="Smart pressure sensors detect when a person gets out of bed and can trigger customized morning routines."
        />
        
        <FeatureCard 
          title="Scheduled Automation" 
          icon={<Clock className="text-green-500" />}
          description="Time-based triggers automatically adjust lighting, temperature, blinds and other devices based on your schedule."
        />
        
        <FeatureCard 
          title="User Recognition" 
          icon={<Users className="text-purple-500" />}
          description="Identifies specific users through gait analysis and customizes routines to individual preferences."
        />
        
        <FeatureCard 
          title="Environmental Control" 
          icon={<Sunrise className="text-yellow-500" />}
          description="Gradually adjusts lighting and blinds to simulate sunrise for a more natural wake-up experience."
        />
        
        <FeatureCard 
          title="Morning Preparation" 
          icon={<Coffee className="text-red-500" />}
          description="Automates kitchen appliances like coffee machines to have refreshments ready when you wake up."
        />
        
        <FeatureCard 
          title="Pathway Safety" 
          icon={<Droplet className="text-cyan-500" />}
          description="Motion-activated lighting ensures safe navigation through the home during early morning hours."
        />
      </div>
      
      <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-100">
        <h4 className="font-medium text-blue-800 mb-1">How to Explore This Demo</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Use the <strong>Demo Visualizer</strong> to see the morning routine in action</li>
          <li>• Switch to the <strong>Devices</strong> tab to view connected smart home devices</li>
          <li>• Check the <strong>Routines</strong> tab to see how automations are configured</li>
          <li>• Explore the components below to understand how each system works</li>
        </ul>
      </div>
    </div>
  );
};

interface FeatureCardProps {
  title: string;
  icon: React.ReactNode;
  description: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ title, icon, description }) => {
  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-center mb-2">
        <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center">
          {icon}
        </div>
        <h4 className="font-medium ml-3">{title}</h4>
      </div>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  );
};

export default QuickGuide; 